// src/store/slices/bootContextSlice.ts
// Boot context state for Central switch-context compliance.
// Central switch-context response is the SOLE authority for workspace/app state.
//
// ARCHITECTURE: BeHeard is a CONSUMER only. Central is the Single Source of Truth.
//
// Types and slice factory come from @levelup/central-auth.
// This file creates the slice, exports actions, and defines app-scoped selectors.

import { createBootContextSlice } from '@levelup/central-auth';
import { createSelector } from '@reduxjs/toolkit';
import type { SuiteApp, SuiteAppExtended } from '@levelup/central-auth';
import { normalizeApps, ensureCurrentApp } from '../../utils/normalizeApps';
import { APP_ID } from '../../config/centralAuth';

// Re-export all shared types for backward compatibility
export type {
  CapabilitiesMap,
  WorkspacePermissions,
  SwitchContextWorkspace,
  SuiteApp,
  SuiteAppExtended,
  CentralAvatar,
  AppTheme,
  AppIconAsset,
  AppAssets,
  UIShellLayout,
  UIShellIconography,
  UIShell,
  SwitchContextMeta,
  SwitchContextUser,
  BootStatus,
  SwitchContextResponse,
  BootContextState,
} from '@levelup/central-auth';

// ============================================================
// Create slice from shared factory
// ============================================================

const { reducer, actions } = createBootContextSlice();

export default reducer;

// ============================================================
// Action Exports
// ============================================================

export const {
  setBootLoading,
  setBootReady,
  setBootWorkspaceClosed,
  setBootUnavailable,
  setBootUnauthenticated,
  resetBoot,
  updateBootContext,
  updateCurrentWorkspaceName,
  updateCurrentWorkspaceAvatar,
} = actions;

// ============================================================
// Selectors (app-specific — use BeHeard's store shape)
// ============================================================

interface BootContextState {
  bootStatus: string;
  currentWorkspace: { id: string; name: string; slug: string; type?: 'personal' | 'business'; role?: string; isPrimary: boolean; scopes?: string[]; capabilities?: Record<string, Record<string, Record<string, boolean>>>; permissions?: { manageMembers: boolean; manageBilling: boolean; transferOwnership: boolean }; avatar?: { type: 'initials' | 'image'; initials?: string; imageUrl?: string } } | null;
  otherWorkspaces: Array<{ id: string; name: string; slug: string; type?: 'personal' | 'business'; role?: string; isPrimary: boolean; avatar?: { type: 'initials' | 'image'; initials?: string; imageUrl?: string } }>;
  // SuiteAppExtended: includes assets.icon (iconUrl, iconKey, iconVersion, monochrome), ui.subtitle, ui.theme
  apps: SuiteAppExtended[];
  user: { id: string; email: string; fullName: string | null; avatarUrl: string | null; platformInternal?: boolean; platformSuperAdmin?: boolean } | null;
  meta: { cacheTtlSeconds?: number; features?: { capabilities?: boolean; workspacePermissions?: boolean; uiShell?: boolean; uiShellAssets?: boolean }; uiShell?: { layout?: { gutter?: number; navWidth?: { collapsed?: number; expanded?: number } }; iconography?: { iconSet?: string } }; uiHints?: { createWorkspaceUrl?: string } } | null;
  error: string | null;
  closedWorkspaceResponse: { otherWorkspaces: Array<{ id: string; name: string }> } | null;
}

type RootWithBootContext = { bootContext: BootContextState };

export const selectBootStatus = (state: RootWithBootContext) => state.bootContext.bootStatus;
export const selectBootApps = (state: RootWithBootContext) => state.bootContext.apps;
export const selectBootOtherWorkspaces = (state: RootWithBootContext) => state.bootContext.otherWorkspaces;
export const selectBootCurrentWorkspace = (state: RootWithBootContext) => state.bootContext.currentWorkspace;
export const selectBootUser = (state: RootWithBootContext) => state.bootContext.user;
export const selectBootMeta = (state: RootWithBootContext) => state.bootContext.meta;
export const selectBootError = (state: RootWithBootContext) => state.bootContext.error;
export const selectBootClosedWorkspaceResponse = (state: RootWithBootContext) =>
  state.bootContext.closedWorkspaceResponse;
export const selectIsBootReady = (state: RootWithBootContext) => state.bootContext.bootStatus === 'ready';

/**
 * Select the create workspace URL from Central's UI hints.
 */
export const selectCreateWorkspaceUrl = (state: RootWithBootContext): string | null =>
  state.bootContext.meta?.uiHints?.createWorkspaceUrl ?? null;

/**
 * Select the current app ID for this application.
 */
export { APP_ID as selectCurrentAppId } from '../../config/centralAuth';

// ============================================================
// Backward-compatible selectors
// (These map old centralAuthSlice boot state to the new bootContextSlice.)
// ============================================================

export const selectBootCompleted = (state: RootWithBootContext) =>
  state.bootContext.bootStatus === 'ready';

export const selectWorkspaceClosed = (state: RootWithBootContext) =>
  state.bootContext.bootStatus === 'workspace_closed';

export const selectCentralUnavailable = (state: RootWithBootContext) =>
  state.bootContext.bootStatus === 'unavailable';

export const selectOtherWorkspaces = (state: RootWithBootContext) =>
  state.bootContext.otherWorkspaces;

export const selectEntitlementsEffective = (state: RootWithBootContext): string[] =>
  state.bootContext.currentWorkspace?.scopes ?? [];

export const selectSuiteAppsRaw = (state: RootWithBootContext): SuiteAppExtended[] =>
  state.bootContext.apps;

// Memoized: only recomputes when the raw apps array reference changes,
// so React-Redux's strict-equality check sees a stable reference between
// renders that don't mutate apps. Prevents "returned a different result
// when called with the same parameters" warnings + unnecessary re-renders.
export const selectSuiteAppsNormalized = createSelector(
  [(state: RootWithBootContext) => state.bootContext.apps],
  (raw) => {
    const mapped: SuiteApp[] = raw.map((app) => ({
      appId: app.appId,
      name: app.displayName ?? app.name,
      webUrl: app.webUrl,
      iconUrl: app.iconUrl,
      mobileScheme: app.mobileScheme,
      status: app.status as SuiteApp['status'],
      order: app.order,
    }));
    const normalized = normalizeApps(mapped);
    return ensureCurrentApp(
      normalized,
      APP_ID,
      'BeHeard',
      typeof window !== 'undefined' ? window.location.origin : '',
    );
  },
);

export const selectSwitchContextMeta = (state: RootWithBootContext) =>
  state.bootContext.meta;
